import { landingFeaturedMarkets } from "../features/landing/components/constants/content";
import {
	LEARN_ENTRIES,
	LEARN_SLUGS,
	buildArticle,
	buildFaqItems,
	createLearnTranslate,
	deriveArticleDescription,
	getLearnEntry,
	isLearnSlug,
} from "../features/learn/content";
import { stripMarkdown } from "./markdown-plain";
import {
	KNOWN_MARKET_NAMES,
	XYZ_DEX_NAME,
	createTradeMarketIdentity,
	createTradeRouteSymbol,
} from "../features/trade/lib/hyperliquid";
import {
	type MarketTranslate,
	getLocalizedPerpetualDisplayName,
} from "../features/trade/lib/market-display";
import {
	type SupportedLocale,
	stripLocalePrefix,
	withLocalePrefix,
} from "./locale";
import { XYZ_MARKET_NAMES } from "../features/trade/lib/xyz-markets";

export const SITE_NAME = "Tenbagger";
// Korean brand spelling — must match Organization.alternateName in index.html.
export const SITE_NAME_KO = "텐배거";

function siteNameForLocale(locale: SupportedLocale) {
	return locale === "ko" ? SITE_NAME_KO : SITE_NAME;
}
export const SITE_URL = "https://tenbagger.xyz";
export const DEFAULT_SOCIAL_IMAGE = "/images/tenbagger_preview.jpg";
export const DEFAULT_TITLE = `${SITE_NAME} | Global Perpetual Trading`;
export const DEFAULT_DESCRIPTION =
	"Trade crypto and global perpetual futures on Tenbagger. Track markets, manage positions, and learn the platform in one interface.";

// Bumped whenever the learn content set changes materially. Used for
// Article.datePublished / dateModified until per-entry timestamps land.
export const LEARN_ARTICLES_PUBLISHED_AT = "2026-04-21";
export const LEARN_ARTICLES_MODIFIED_AT = "2026-07-09";

type SeoHeadOptions = {
	title: string;
	description: string;
	path: string;
	robots?: string;
};

export type RuntimeSeoContent = {
	title: string;
	description: string;
	path: string;
	robots: string;
};

function normalizePath(path: string) {
	if (!path) return "/";
	const prefixed = path.startsWith("/") ? path : `/${path}`;
	// Trailing-slash variants (`/learn/usdc/`) must converge on the slash-less
	// canonical — Google crawls both forms and CF Pages 308s the slashed one.
	const stripped = prefixed.replace(/\/+$/, "");
	return stripped === "" ? "/" : stripped;
}

export function getCanonicalUrl(path: string) {
	return new URL(normalizePath(path), SITE_URL).toString();
}

/**
 * Whether a logical path has localized (`/ko/...`) URLs. Phase 1: only landing +
 * learn — so hreflang/sitemap never advertise a /ko trade URL that would 404.
 */
function isLocalizablePath(logicalPath: string): boolean {
	if (logicalPath === "/" || logicalPath === "/learn") return true;
	if (logicalPath.startsWith("/learn/")) {
		return isLearnSlug(logicalPath.slice("/learn/".length));
	}
	return false;
}

export function buildSeoHead({
	title,
	description,
	path,
	robots = "index, follow",
}: SeoHeadOptions) {
	const canonical = getCanonicalUrl(path);
	const image = getCanonicalUrl(DEFAULT_SOCIAL_IMAGE);

	return {
		meta: [
			{ title },
			{ name: "description", content: description },
			{ name: "robots", content: robots },
			{ property: "og:type", content: "website" },
			{ property: "og:site_name", content: SITE_NAME },
			{ property: "og:title", content: title },
			{ property: "og:description", content: description },
			{ property: "og:url", content: canonical },
			{ property: "og:image", content: image },
			{ name: "twitter:card", content: "summary" },
			{ name: "twitter:title", content: title },
			{ name: "twitter:description", content: description },
			{ name: "twitter:image", content: image },
		],
		links: [{ rel: "canonical", href: canonical }],
	};
}

export function buildTradeSeoHead(routeSymbol: string) {
	const market = createTradeMarketIdentity(routeSymbol);
	const title = `${market.displaySymbol} | ${SITE_NAME}`;
	const description = `Trade ${market.displayName} on ${SITE_NAME}. Track charts, order flow, positions, and perpetual market activity from a single workspace.`;

	return buildSeoHead({
		title,
		description,
		path: `/trade/${market.routeSymbol}`,
	});
}

export const PUBLIC_SITEMAP_PATHS = ["/", "/learn", "/baggers"] as const;

export const PUBLIC_TRADE_SITEMAP_SYMBOLS = Array.from(
	new Set([
		...Object.keys(KNOWN_MARKET_NAMES).map((coin) => createTradeRouteSymbol(coin)),
		...Object.keys(XYZ_MARKET_NAMES).map((coin) =>
			createTradeRouteSymbol(coin, XYZ_DEX_NAME),
		),
		...landingFeaturedMarkets.map((market) => market.routeSymbol),
	]),
).sort((left, right) => left.localeCompare(right));

export function getPublicSitemapPaths() {
	return [
		...PUBLIC_SITEMAP_PATHS,
		...LEARN_SLUGS.map((slug) => `/learn/${slug}`),
		...PUBLIC_TRADE_SITEMAP_SYMBOLS.map((symbol) => `/trade/${symbol}`),
	];
}

export type SitemapUrlEntry = {
	/** Logical (locale-stripped) path — drives changefreq/priority. */
	logicalPath: string;
	/** Absolute canonical URL for this `<loc>`. */
	loc: string;
	/** hreflang alternates (empty for non-localized pages). */
	alternates: Array<{ hreflang: string; href: string }>;
};

/**
 * Sitemap entries with hreflang alternates. Localized paths (Phase 1: landing +
 * learn) emit both en + ko `<url>` rows, each carrying en/ko/x-default
 * alternates; everything else stays a single en row.
 */
export function getSitemapUrlEntries(): SitemapUrlEntry[] {
	const entries: SitemapUrlEntry[] = [];
	for (const path of getPublicSitemapPaths()) {
		if (isLocalizablePath(path)) {
			const enHref = getCanonicalUrl(path);
			const koHref = getCanonicalUrl(withLocalePrefix(path, "ko"));
			const alternates = [
				{ hreflang: "en", href: enHref },
				{ hreflang: "ko", href: koHref },
				{ hreflang: "x-default", href: enHref },
			];
			entries.push({ logicalPath: path, loc: enHref, alternates });
			entries.push({ logicalPath: path, loc: koHref, alternates });
		} else {
			entries.push({
				logicalPath: path,
				loc: getCanonicalUrl(path),
				alternates: [],
			});
		}
	}
	return entries;
}

function decodeTradeSymbolFromPath(pathname: string) {
	return decodeURIComponent(pathname.slice("/trade/".length));
}

function getDefaultRuntimeSeo(t: MarketTranslate): RuntimeSeoContent {
	return {
		title: t("seo.home.title"),
		description: t("seo.home.description"),
		path: "/",
		robots: "index, follow",
	};
}

export function getRuntimeSeoContent({
	pathname,
	t,
	locale = "en",
}: {
	pathname: string;
	t: MarketTranslate;
	locale?: SupportedLocale;
}): RuntimeSeoContent {
	// Resolve on the logical (prefix-stripped) path, then re-apply the locale
	// prefix so `/ko/...` pages self-canonicalize — but ONLY for paths that
	// actually have localized URLs. Prefixing a non-localizable path (/terms,
	// /sign, /trade/*, /portfolio, /referrals) would canonicalize to a /ko/...
	// URL that 404s, so those keep a single (English) canonical for every locale.
	// normalizePath first: a trailing slash would miss every `===` branch below
	// and fall through to the home defaults (canonical `/`, noindex lost).
	const base = resolveRuntimeSeo(
		stripLocalePrefix(normalizePath(pathname)),
		t,
		locale,
	);
	const path = isLocalizablePath(base.path)
		? withLocalePrefix(base.path, locale)
		: base.path;
	return { ...base, path };
}

function resolveRuntimeSeo(
	pathname: string,
	t: MarketTranslate,
	locale: SupportedLocale,
): RuntimeSeoContent {
	if (pathname === "/learn") {
		return {
			title: t("seo.learn.title"),
			description: t("seo.learn.description"),
			path: pathname,
			robots: "index, follow",
		};
	}

	if (pathname.startsWith("/learn/")) {
		const slug = pathname.slice("/learn/".length);
		if (isLearnSlug(slug)) {
			const translate = createLearnTranslate((key) => t(key), locale);
			const article = buildArticle(translate, slug);
			if (article) {
				return {
					title: `${article.title} | ${siteNameForLocale(locale)}`,
					description: deriveArticleDescription(article.body),
					path: `/learn/${article.slug}`,
					robots: "index, follow",
				};
			}
		}
	}

	if (pathname === "/portfolio") {
		return {
			title: t("seo.portfolio.title"),
			description: t("seo.portfolio.description"),
			path: pathname,
			robots: "noindex, nofollow",
		};
	}

	if (pathname === "/referrals") {
		return {
			title: t("seo.referrals.title"),
			description: t("seo.referrals.description"),
			path: pathname,
			robots: "noindex, nofollow",
		};
	}

	// /terms + /sign are English-only static pages (no Tolgee, no /ko URL). They
	// self-canonicalize with hardcoded metadata instead of falling through to the
	// home defaults (which would canonicalize them to `/` — a duplicate of home).
	if (pathname === "/terms") {
		return {
			title: `Terms of Service | ${SITE_NAME}`,
			description:
				"Tenbagger Terms of Service. A front-end interface to the Hyperliquid DEX — Tenbagger holds no custody, runs no exchange, and gives no financial advice.",
			path: "/terms",
			robots: "index, follow",
		};
	}

	if (pathname === "/sign") {
		return {
			title: `Sign Message | ${SITE_NAME}`,
			description:
				"Sign a message with your Tenbagger wallet to verify wallet ownership.",
			path: "/sign",
			robots: "noindex, nofollow",
		};
	}

	if (pathname === "/baggers") {
		return {
			title: t("seo.baggers.title"),
			description: t("seo.baggers.description"),
			path: "/baggers",
			robots: "index, follow",
		};
	}

	if (pathname.startsWith("/trade/")) {
		const routeSymbol = decodeTradeSymbolFromPath(pathname);
		const market = createTradeMarketIdentity(routeSymbol);
		const localizedMarketName = getLocalizedPerpetualDisplayName({
			coin: market.coin,
			dex: market.dex,
			t,
		});

		return {
			title: t("seo.trade.title", { symbol: market.displaySymbol }),
			description: t("seo.trade.description", {
				market: localizedMarketName,
			}),
			path: `/trade/${market.routeSymbol}`,
			robots: "index, follow",
		};
	}

	return getDefaultRuntimeSeo(t);
}

function getOgLocale(locale: SupportedLocale) {
	switch (locale) {
		case "ko":
			return "ko_KR";
		case "zh":
			return "zh_CN";
		default:
			return "en_US";
	}
}

function upsertMeta({
	attribute,
	key,
	content,
}: {
	attribute: "name" | "property";
	key: string;
	content: string;
}) {
	const existing = Array.from(
		document.head.querySelectorAll<HTMLMetaElement>(`meta[${attribute}]`),
	).find((element) => element.getAttribute(attribute) === key);

	const meta = existing ?? document.createElement("meta");
	meta.setAttribute(attribute, key);
	meta.setAttribute("content", content);

	if (!existing) {
		document.head.appendChild(meta);
	}
}

function upsertCanonicalLink(href: string) {
	const existing = document.head.querySelector<HTMLLinkElement>(
		'link[rel="canonical"]',
	);
	const link = existing ?? document.createElement("link");
	link.setAttribute("rel", "canonical");
	link.setAttribute("href", href);

	if (!existing) {
		document.head.appendChild(link);
	}
}

// Emit hreflang alternates from the LOGICAL path. Localized pages (Phase 1:
// landing + learn) get en + ko + x-default, each pointing at its own locale URL
// (self-canonical). Non-localized pages stay en-only — we never advertise a
// /ko URL that 404s.
function syncHreflangLinks(logicalPath: string) {
	for (const node of document.head.querySelectorAll<HTMLLinkElement>(
		'link[rel="alternate"][hreflang]',
	)) {
		node.remove();
	}

	const enHref = getCanonicalUrl(logicalPath);
	const alternates: Array<{ hreflang: string; href: string }> = [
		{ hreflang: "en", href: enHref },
	];
	if (isLocalizablePath(logicalPath)) {
		alternates.push({
			hreflang: "ko",
			href: getCanonicalUrl(withLocalePrefix(logicalPath, "ko")),
		});
		alternates.push({ hreflang: "x-default", href: enHref });
	}

	for (const { hreflang, href } of alternates) {
		const link = document.createElement("link");
		link.setAttribute("rel", "alternate");
		link.setAttribute("hreflang", hreflang);
		link.setAttribute("href", href);
		document.head.appendChild(link);
	}
}

const ROUTE_JSON_LD_ID = "route-json-ld";

function upsertRouteJsonLd(payload: object | null) {
	const existing = document.head.querySelector<HTMLScriptElement>(
		`script[data-seo-id="${ROUTE_JSON_LD_ID}"]`,
	);

	if (payload === null) {
		existing?.remove();
		return;
	}

	const script = existing ?? document.createElement("script");
	script.setAttribute("type", "application/ld+json");
	script.dataset.seoId = ROUTE_JSON_LD_ID;
	script.textContent = JSON.stringify(payload);

	if (!existing) {
		document.head.appendChild(script);
	}
}

export function buildTradeJsonLd({
	pathname,
	title,
	description,
}: {
	pathname: string;
	title: string;
	description: string;
}) {
	const routeSymbol = decodeTradeSymbolFromPath(pathname);
	const market = createTradeMarketIdentity(routeSymbol);
	const canonical = getCanonicalUrl(`/trade/${market.routeSymbol}`);

	return {
		"@context": "https://schema.org",
		"@type": "FinancialProduct",
		name: title,
		description,
		url: canonical,
		category: "Perpetual Futures",
		provider: { "@id": `${SITE_URL}/#organization` },
		tickerSymbol: market.displaySymbol,
	};
}

// Builds a FAQPage node from an entry's structured FAQ pairs (answers stripped
// to plain text). Returned as a @graph sibling of Article — AI answer engines
// weight FAQPage Q&A highly for citation, and it stays valid schema.org
// regardless of Google's (now limited) FAQ rich-result display.
function buildLearnFaqPageNode({
	slug,
	t,
	locale,
	url,
}: {
	slug: string;
	t: MarketTranslate;
	locale: SupportedLocale;
	url: string;
}) {
	const entry = getLearnEntry(slug);
	if (!entry || entry.faqs.length === 0) return null;

	const translate = createLearnTranslate((key) => t(key), locale);
	const items = buildFaqItems(translate, entry);
	if (items.length === 0) return null;

	return {
		"@type": "FAQPage",
		"@id": `${url}#faq`,
		mainEntity: items.map(({ question, answer }) => ({
			"@type": "Question",
			name: question,
			acceptedAnswer: { "@type": "Answer", text: stripMarkdown(answer) },
		})),
	};
}

export function buildLearnArticleJsonLd({
	slug,
	t,
	locale,
}: {
	slug: string;
	t: MarketTranslate;
	locale: SupportedLocale;
}) {
	if (!isLearnSlug(slug)) return null;
	const translate = createLearnTranslate((key) => t(key), locale);
	const article = buildArticle(translate, slug);
	if (!article) return null;

	const url = getCanonicalUrl(withLocalePrefix(`/learn/${article.slug}`, locale));
	const image = getCanonicalUrl(DEFAULT_SOCIAL_IMAGE);

	const articleNode = {
		"@type": "Article",
		mainEntityOfPage: { "@type": "WebPage", "@id": url },
		url,
		headline: article.title,
		description: deriveArticleDescription(article.body),
		articleBody: stripMarkdown(article.body),
		image,
		datePublished: LEARN_ARTICLES_PUBLISHED_AT,
		dateModified: LEARN_ARTICLES_MODIFIED_AT,
		author: {
			"@type": "Organization",
			name: SITE_NAME,
			url: SITE_URL,
		},
		publisher: { "@id": `${SITE_URL}/#organization` },
	};

	const faqNode = buildLearnFaqPageNode({ slug, t, locale, url });
	if (!faqNode) {
		return { "@context": "https://schema.org", ...articleNode };
	}

	return {
		"@context": "https://schema.org",
		"@graph": [articleNode, faqNode],
	};
}

export function buildLearnHubCollectionJsonLd(
	t: MarketTranslate,
	locale: SupportedLocale = "en",
) {
	const url = getCanonicalUrl(withLocalePrefix("/learn", locale));
	return {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		"@id": url,
		url,
		name: t("seo.learn.title"),
		description: t("seo.learn.description"),
		isPartOf: { "@id": `${SITE_URL}/#website` },
		mainEntity: {
			"@type": "ItemList",
			itemListElement: LEARN_ENTRIES.map((entry, index) => ({
				"@type": "ListItem",
				position: index + 1,
				url: getCanonicalUrl(withLocalePrefix(`/learn/${entry.slug}`, locale)),
				name: t(entry.titleKey),
			})),
		},
	};
}

export function syncDocumentSeo({
	locale,
	pathname,
	t,
}: {
	locale: SupportedLocale;
	pathname: string;
	t: MarketTranslate;
}) {
	if (typeof document === "undefined") return;

	const seo = getRuntimeSeoContent({ pathname, t, locale });
	// Same normalization as getRuntimeSeoContent — hreflang + JSON-LD branch on
	// this path, and a trailing slash would drop ko alternates and the JSON-LD.
	const logicalPath = stripLocalePrefix(normalizePath(pathname));
	const canonical = getCanonicalUrl(seo.path);
	const image = getCanonicalUrl(DEFAULT_SOCIAL_IMAGE);

	document.title = seo.title;
	upsertMeta({
		attribute: "name",
		key: "description",
		content: seo.description,
	});
	upsertMeta({
		attribute: "name",
		key: "robots",
		content: seo.robots,
	});
	upsertMeta({
		attribute: "property",
		key: "og:type",
		content: "website",
	});
	upsertMeta({
		attribute: "property",
		key: "og:site_name",
		content: SITE_NAME,
	});
	upsertMeta({
		attribute: "property",
		key: "og:title",
		content: seo.title,
	});
	upsertMeta({
		attribute: "property",
		key: "og:description",
		content: seo.description,
	});
	upsertMeta({
		attribute: "property",
		key: "og:url",
		content: canonical,
	});
	upsertMeta({
		attribute: "property",
		key: "og:image",
		content: image,
	});
	upsertMeta({
		attribute: "property",
		key: "og:image:type",
		content: "image/jpeg",
	});
	upsertMeta({
		attribute: "property",
		key: "og:image:width",
		content: "1200",
	});
	upsertMeta({
		attribute: "property",
		key: "og:image:height",
		content: "630",
	});
	upsertMeta({
		attribute: "property",
		key: "og:image:alt",
		content: seo.title,
	});
	upsertMeta({
		attribute: "property",
		key: "og:locale",
		content: getOgLocale(locale),
	});
	upsertMeta({
		attribute: "name",
		key: "twitter:card",
		content: "summary_large_image",
	});
	upsertMeta({
		attribute: "name",
		key: "twitter:title",
		content: seo.title,
	});
	upsertMeta({
		attribute: "name",
		key: "twitter:description",
		content: seo.description,
	});
	upsertMeta({
		attribute: "name",
		key: "twitter:image",
		content: image,
	});
	upsertCanonicalLink(canonical);
	syncHreflangLinks(logicalPath);

	if (logicalPath.startsWith("/trade/")) {
		upsertRouteJsonLd(
			buildTradeJsonLd({
				pathname: logicalPath,
				title: seo.title,
				description: seo.description,
			}),
		);
	} else if (logicalPath.startsWith("/learn/")) {
		const slug = logicalPath.slice("/learn/".length);
		upsertRouteJsonLd(buildLearnArticleJsonLd({ slug, t, locale }));
	} else if (logicalPath === "/learn") {
		upsertRouteJsonLd(buildLearnHubCollectionJsonLd(t, locale));
	} else {
		upsertRouteJsonLd(null);
	}
}
